import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Table, Tag, Space, Button, Alert } from 'antd';
import { 
  PrinterOutlined, 
  ExperimentOutlined, 
  LinkOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  WarningOutlined,
  PlusOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface Printer {
  id: number;
  name: string;
  model: string;
  status: string;
  location: string;
}

interface Material {
  id: number;
  name: string;
  color: string;
}

interface DashboardStats {
  printerCount: number;
  materialCount: number;
  availablePrinters: number;
  printingPrinters: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    printerCount: 0,
    materialCount: 0,
    availablePrinters: 0,
    printingPrinters: 0,
  });
  const [recentPrinters, setRecentPrinters] = useState<Printer[]>([]);
  const [recentMaterials, setRecentMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // 获取打印机数据
      const printersResponse = await fetch('/api/admin/printers?limit=5', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // 获取材料数据
      const materialsResponse = await fetch('/api/admin/materials?limit=5', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // 获取打印机状态统计
      const statusResponse = await fetch('/api/printers_status');
      
      if (printersResponse.ok && materialsResponse.ok && statusResponse.ok) {
        const printersData = await printersResponse.json();
        const materialsData = await materialsResponse.json();
        const statusData = await statusResponse.json();
        
        // 更新统计数据
        setStats({
          printerCount: statusData.total || 0,
          materialCount: materialsData.length || 0,
          availablePrinters: statusData.available || 0,
          printingPrinters: statusData.printing || 0,
        });
        
        // 更新最近打印机
        setRecentPrinters(printersData.slice(0, 5).map((printer: any) => ({
          id: printer.id,
          name: printer.name,
          model: printer.model,
          status: printer.status,
          location: printer.location,
        })));
        
        // 更新最近材料
        setRecentMaterials(materialsData.slice(0, 5).map((material: any) => ({
          id: material.id,
          name: material.name,
          color: material.color,
        })));
      }
    } catch (error) {
      console.error('获取仪表板数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'green';
      case 'printing': return 'blue';
      case 'maintenance': return 'orange';
      case 'idle': return 'gray';
      default: return 'red';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '可用';
      case 'printing': return '打印中';
      case 'maintenance': return '维护中';
      case 'idle': return '空闲';
      default: return '故障';
    }
  };

  const printerColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Printer) => (
        <Space>
          <PrinterOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '型号',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Printer) => (
        <Button
          type="link"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/admin/printers/${record.id}`)}
        >
          管理
        </Button>
      ),
    },
  ];

  const materialColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Material) => (
        <Space>
          <ExperimentOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => (
        <Space>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: color,
            borderRadius: '2px',
            border: '1px solid #d9d9d9',
          }} />
          <Text>{color}</Text>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Material) => (
        <Button
          type="link"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/admin/materials/${record.id}`)}
        >
          编辑
        </Button>
      ),
    },
  ];

  // 添加调试信息
  console.log('AdminDashboard rendered', { stats, recentPrinters, recentMaterials, loading });
  
  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>管理仪表板</Title>
      
      <Alert
        message="欢迎使用3D打印管理后台"
        description="在这里您可以管理打印机、材料以及它们之间的关联关系。"
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />
      
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="打印机总数"
              value={stats.printerCount}
              prefix={<PrinterOutlined style={{ color: '#d32f2f' }} />}
              valueStyle={{ color: '#d32f2f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="材料总数"
              value={stats.materialCount}
              prefix={<ExperimentOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="可用打印机"
              value={stats.availablePrinters}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="正在打印"
              value={stats.printingPrinters}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>
      
      {/* 快速操作 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card title="快速操作">
            <Space wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/admin/printers/new')}
                style={{ background: '#d32f2f', borderColor: '#d32f2f' }}
              >
                添加打印机
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/admin/materials/new')}
              >
                添加材料
              </Button>
              <Button
                type="default"
                icon={<LinkOutlined />}
                onClick={() => navigate('/admin/associations')}
              >
                管理关联
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
      
      {/* 最近打印机和材料 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title="最近打印机" 
            extra={
              <Button type="link" onClick={() => navigate('/admin/printers')}>
                查看全部
              </Button>
            }
          >
            <Table
              columns={printerColumns}
              dataSource={recentPrinters}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="最近材料"
            extra={
              <Button type="link" onClick={() => navigate('/admin/materials')}>
                查看全部
              </Button>
            }
          >
            <Table
              columns={materialColumns}
              dataSource={recentMaterials}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
      </Row>
      
      {/* 系统提示 */}
      <Row style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card>
            <Title level={5}>使用提示</Title>
            <ul style={{ color: '#666', lineHeight: '1.8' }}>
              <li>添加打印机时，可以上传缩略图和介绍视频</li>
              <li>为打印机添加材料关联后，可以设置当前使用的耗材</li>
              <li>删除材料前，请确保没有打印机正在使用该材料</li>
              <li>所有操作都需要管理员权限，请妥善保管登录信息</li>
            </ul>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;
